import { useEffect, useState } from 'react';

import { createFileRoute, redirect } from '@tanstack/react-router';

import {
  jobsConfigApi,
  type JobCatalogItem,
  type JobRunSummary,
} from '@/api/queries/jobsConfig';
import { isSuperAdmin, useTenantStore } from '@/auth/tenantStore';
import { ErrorComponent } from '@/components/common/ErrorComponent';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { SettingField } from '@/components/jobs/SettingField';
import { formatDuration, formatTotals, runStatusTone } from '@/components/jobs/runUtils';
import { useSetBreadcrumbs } from '@/components/layout/breadcrumbs';
import {
  Button,
  Checkbox,
  Field,
  Input,
  Modal,
  Pagination,
  Select,
  StatusPill,
  Table,
  useToast,
  type TableColumn,
} from '@/components/ui';
import { formatDateTime } from '@/utils/formatDate';

// Friendly schedule presets; "custom" falls back to a raw cron field.
const PRESETS: { value: string; label: string; cron: string }[] = [
  { value: 'hourly', label: 'Hourly', cron: '0 * * * *' },
  { value: 'every2h', label: 'Every 2 hours', cron: '0 */2 * * *' },
  { value: 'every6h', label: 'Every 6 hours', cron: '0 */6 * * *' },
  { value: 'daily', label: 'Daily (midnight)', cron: '0 0 * * *' },
  { value: 'weekly', label: 'Weekly (Sun 00:00)', cron: '0 0 * * 0' },
  { value: 'custom', label: 'Custom…', cron: '' },
];

function presetForCron(cron: string): string {
  return PRESETS.find((p) => p.cron === cron)?.value ?? 'custom';
}

function JobDetailRoute() {
  const { key } = Route.useParams();
  const [tab, setTab] = useState<'settings' | 'runs'>('settings');
  const { data: job, isPending } = jobsConfigApi.useJobConfig(key);

  useSetBreadcrumbs([
    { label: 'Admin' },
    { label: 'Jobs', to: '/admin/jobs' },
    { label: 'Configuration', to: '/admin/jobs/settings' },
    { label: job?.displayName ?? key },
  ]);

  if (isPending || !job) return <LoadingSpinner />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>{job.displayName}</h1>
        <span style={{ fontSize: 13, color: 'var(--text-3)', maxWidth: 640 }}>{job.description}</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 4, borderBottom: '1px solid var(--border)' }}>
        {(['settings', 'runs'] as const).map((t) => {
          const active = tab === t;
          return (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              style={{
                padding: '8px 14px',
                background: 'transparent',
                border: 'none',
                borderBottom: active ? '2px solid var(--brand)' : '2px solid transparent',
                color: active ? 'var(--text-1)' : 'var(--text-3)',
                fontWeight: active ? 600 : 400,
                cursor: 'pointer',
              }}
            >
              {t === 'settings' ? 'Settings' : 'Run history'}
            </button>
          );
        })}
      </div>

      {tab === 'settings' ? <SettingsTab job={job} /> : <RunsTab jobKey={key} />}
    </div>
  );
}

function SettingsTab({ job }: { job: JobCatalogItem }) {
  const update = jobsConfigApi.useUpdateJobConfig();
  const validate = jobsConfigApi.useValidateCron();
  const toast = useToast();

  const [enabled, setEnabled] = useState(job.enabled);
  const [preset, setPreset] = useState(presetForCron(job.cron));
  const [cron, setCron] = useState(job.cron);
  const [settings, setSettings] = useState<Record<string, unknown>>({ ...job.settings });
  const [preview, setPreview] = useState<{ valid: boolean; error?: string | null; next: string[] }>({
    valid: true,
    next: [],
  });

  // Validate + preview whenever the cron expression changes.
  useEffect(() => {
    let cancelled = false;
    if (!cron.trim()) {
      setPreview({ valid: false, error: 'Cron expression is required.', next: [] });
      return;
    }
    validate
      .mutateAsync(cron)
      .then((r) => {
        if (!cancelled) setPreview(r);
      })
      .catch(() => {
        if (!cancelled) setPreview({ valid: false, error: 'Validation failed.', next: [] });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cron]);

  const onPresetChange = (value: string) => {
    setPreset(value);
    const p = PRESETS.find((x) => x.value === value);
    if (p && p.value !== 'custom') setCron(p.cron);
  };

  const onSave = async () => {
    try {
      await update.mutateAsync({
        key: job.key,
        body: {
          enabled,
          cron: cron === job.defaultCron ? null : cron,
          settings,
        },
      });
      toast.show({ title: 'Job configuration saved', severity: 'success' });
    } catch (err) {
      toast.show({
        title: 'Failed to save',
        description: err instanceof Error ? err.message : undefined,
        severity: 'error',
      });
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 640 }}>
      <Field label="Status">
        <Checkbox checked={enabled} onChange={setEnabled} label="Enabled" />
        {!enabled && (
          <span style={{ fontSize: 12, color: 'var(--amber, var(--text-3))' }}>
            Disabled jobs are removed from the scheduler and will not run.
          </span>
        )}
      </Field>

      <Field label="Schedule" hint="Pick a preset or choose “Custom…” to enter a cron expression.">
        <Select
          value={preset}
          onChange={(e) => onPresetChange(e.target.value)}
          options={PRESETS.map((p) => ({ value: p.value, label: p.label }))}
        />
      </Field>

      <Field
        label="Cron expression"
        error={!preview.valid ? (preview.error ?? 'Invalid cron expression.') : undefined}
        hint={
          preview.valid && preview.next.length > 0
            ? `Next runs: ${preview.next.slice(0, 3).map((d) => formatDateTime(d)).join(' · ')}`
            : undefined
        }
      >
        <Input
          value={cron}
          error={!preview.valid}
          onChange={(e) => {
            setCron(e.target.value);
            setPreset(presetForCron(e.target.value));
          }}
          placeholder="0 * * * *"
        />
      </Field>

      {job.settingsSchema.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--text-2)' }}>Behaviour</h3>
          {job.settingsSchema.map((field) => (
            <SettingField
              key={field.key}
              field={field}
              value={settings[field.key]}
              onChange={(v) => setSettings((prev) => ({ ...prev, [field.key]: v }))}
            />
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <Button variant="primary" onClick={onSave} disabled={!preview.valid || update.isPending}>
          Save changes
        </Button>
      </div>
    </div>
  );
}

function RunsTab({ jobKey }: { jobKey: string }) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [openRunId, setOpenRunId] = useState<string | null>(null);

  const { data, isPending } = jobsConfigApi.useJobRuns(jobKey, {
    from: page * pageSize,
    count: pageSize,
  });

  const columns: TableColumn<JobRunSummary>[] = [
    {
      id: 'status',
      header: 'Status',
      cell: (r) => <StatusPill status={runStatusTone(r.status)}>{r.status}</StatusPill>,
      width: 120,
    },
    { id: 'started', header: 'Started', cell: (r) => formatDateTime(r.startedAt) ?? '—', width: 180 },
    { id: 'duration', header: 'Duration', cell: (r) => formatDuration(r.durationMs), width: 110 },
    { id: 'trigger', header: 'Trigger', cell: (r) => r.triggeredBy, width: 100 },
    {
      id: 'totals',
      header: 'Results',
      cell: (r) => <span style={{ fontSize: 12, color: 'var(--text-2)' }}>{formatTotals(r.totals)}</span>,
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Table
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(r) => r.id}
        loading={isPending}
        emptyMessage="No runs recorded yet."
        onRowClick={(r) => setOpenRunId(r.id)}
      />
      <Pagination
        page={page}
        pageSize={pageSize}
        total={data?.total ?? 0}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPage(0);
          setPageSize(size);
        }}
      />
      {openRunId && <RunDetailModal runId={openRunId} onClose={() => setOpenRunId(null)} />}
    </div>
  );
}

function RunDetailModal({ runId, onClose }: { runId: string; onClose: () => void }) {
  const { data: run, isPending } = jobsConfigApi.useJobRun(runId);

  return (
    <Modal open onClose={onClose} title="Run detail" size="lg">
      {isPending || !run ? (
        <LoadingSpinner />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            <Meta label="Status">
              <StatusPill status={runStatusTone(run.status)}>{run.status}</StatusPill>
            </Meta>
            <Meta label="Trigger">{run.triggeredBy}</Meta>
            <Meta label="Started">{formatDateTime(run.startedAt) ?? '—'}</Meta>
            <Meta label="Finished">{formatDateTime(run.finishedAt) ?? '—'}</Meta>
            <Meta label="Duration">{formatDuration(run.durationMs)}</Meta>
            <Meta label="Worker">{run.workerId ?? '—'}</Meta>
          </div>

          {run.error && (
            <div
              style={{
                padding: 12,
                borderRadius: 'var(--radius-md)',
                background: 'var(--surface-2)',
                color: 'var(--red, var(--text-2))',
                fontSize: 13,
              }}
            >
              {run.error}
            </div>
          )}

          <div>
            <h3 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600, color: 'var(--text-2)' }}>Totals</h3>
            <div style={{ fontSize: 13, color: 'var(--text-2)' }}>{formatTotals(run.totals)}</div>
          </div>

          <div>
            <h3 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600, color: 'var(--text-2)' }}>
              Per-tenant breakdown
            </h3>
            {run.tenants.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--text-3)' }}>No tenant changes recorded.</div>
            ) : (
              <Table
                columns={[
                  {
                    id: 'tenant',
                    header: 'Tenant',
                    cell: (t) => t.tenantName ?? t.tenantId,
                  },
                  {
                    id: 'changes',
                    header: 'Changes',
                    cell: (t) => (
                      <span style={{ fontSize: 12, color: 'var(--text-2)' }}>{formatTotals(t.counters)}</span>
                    ),
                  },
                  {
                    id: 'note',
                    header: 'Note',
                    cell: (t) => (t.note ? <span style={{ color: 'var(--text-3)' }}>{t.note}</span> : '—'),
                  },
                ]}
                rows={run.tenants}
                rowKey={(t) => t.tenantId}
              />
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

function Meta({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 120 }}>
      <span style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </span>
      <span style={{ fontSize: 13, color: 'var(--text-1)' }}>{children}</span>
    </div>
  );
}

export const Route = createFileRoute('/admin/jobs/$key')({
  beforeLoad: () => {
    if (!isSuperAdmin(useTenantStore.getState())) {
      throw redirect({ to: '/contacts', search: { page: 0, pageSize: 25 } });
    }
  },
  component: JobDetailRoute,
  errorComponent: ErrorComponent,
});
